import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Button from 'reactstrap/lib/Button';
import { logger } from 'client/utils/logger/logger';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingConstant } from 'client/tracking/constant';
import { LoadingSpinner } from 'site-modules/profile/components/shared/loading-spinner/loading-spinner';
import { settingsWrapperComponent } from 'site-modules/profile/components/settings/settings-wrapper/settings-wrapper';
import { EmailSubscriptionPoint } from 'site-modules/profile/components/settings/email-subscriptions/email-subscription-point';
import {
  createDataToSubscriptions,
  EMAIL_SUBSCRIPTIONS,
  filterChangedValues,
  getIdmValues,
} from 'site-modules/shared/utils/profile/email-subscriptions-helper';

function EmailSubscriptionsUI({
  showActiveSpinner,
  isDeletedUser = false,
  isEditMode = false,
  toggleMode,
  profileData,
  saveChanges,
}) {
  const [state, setState] = useState({ ...getIdmValues(profileData) });
  const initialState = useRef({ ...state });

  function onChange(checked, event, id) {
    TrackingHandler.fireEvent(TrackingConstant.UPDATE_EMAIL_SUBSCRIPTIONS, {
      event_data: {
        action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
        creative_id: id,
        value: checked ? 'subscribe' : 'unsubscribe',
      },
    });
    setState({ ...state, [id]: checked });
  }

  async function onSubmit(e) {
    e.preventDefault();
    const changedValues = filterChangedValues(initialState.current, state);

    const changedValuesKeys = Object.keys(changedValues);
    if (!changedValuesKeys.length) return;

    const trueValues = changedValuesKeys.filter(id => changedValues[id]);
    const falseValues = changedValuesKeys.filter(id => !changedValues[id]);

    try {
      await Promise.all(
        [createDataToSubscriptions(falseValues, 'unsubscribe'), createDataToSubscriptions(trueValues, 'subscribe')]
          .filter(data => data)
          .map(({ values, method }) => saveChanges(values, method))
      );

      initialState.current = { ...state };
      toggleMode();
    } catch (error) {
      logger.error(error);
    }
  }

  useEffect(() => {
    setState(initialState.current);
  }, [isEditMode]);

  return (
    <form className={classnames({ 'form-disabled': showActiveSpinner })} onSubmit={onSubmit}>
      {EMAIL_SUBSCRIPTIONS.map(row => (
        <EmailSubscriptionPoint
          className="mb-1"
          title={row.title}
          id={row.id}
          onChange={onChange}
          isChecked={state[row.id]}
          isEditMode={isEditMode}
          key={row.id}
        />
      ))}

      {isEditMode && (
        <div className="mt-2 text-end">
          <Button size="sm" color="link" className="text-capitalize size-16 me-0_5" onClick={toggleMode}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            color="outline-primary-b"
            className="size-16"
            disabled={showActiveSpinner || isDeletedUser}
            data-tracking-id="update_email_subscriptions"
          >
            Save
            {showActiveSpinner && <LoadingSpinner wrapperClassNames="d-inline-block ps-0_25" />}
          </Button>
        </div>
      )}
    </form>
  );
}

EmailSubscriptionsUI.propTypes = {
  saveChanges: PropTypes.func.isRequired,
  profileData: PropTypes.shape({}).isRequired,
  showActiveSpinner: PropTypes.bool.isRequired,
  isDeletedUser: PropTypes.bool,
  isEditMode: PropTypes.bool,
  toggleMode: PropTypes.func.isRequired,
};

export const EmailSubscriptions = settingsWrapperComponent(EmailSubscriptionsUI);
